import React, {forwardRef, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import {CustomInput} from './customInputComponent';
import {Controller} from 'react-hook-form';
import {formBlockDefaultProps, formBlockTypes} from './formBlockProps';
import {IconButton, InputAdornment} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import fieldFormatter from '../../utils/fieldsToNormalize';
import {useFormContext} from 'react-hook-form';

export const CustomSelect = forwardRef((props, ref)=> {
  const formMethods = useFormContext();
  const [currentSelected, setCurrentSelected] = useState('');

  const effectiveAllowNone = props.allowNone || props.noneStoresUndefined;
  const showEmptyDisplay = props.allowNone || props.noneStoresUndefined;

  const getOptionsToRender = ()=> {
    const options = [];
    if (props.noneStoresUndefined) {
      options.push(
        <MenuItem key="__noneStoresUndefined_empty" value="" sx={{display: 'none'}} aria-hidden tabIndex={-1}>
          {'\u00A0'}
        </MenuItem>
      );
    }
    const currOptions = [...props.options];
    const additionalOption = verifyValueAndOptions();
    if(additionalOption) {
      currOptions.unshift(additionalOption);
    }
    currOptions.forEach((e, idx)=> {
      options.push(<MenuItem value={e.value} key={`${e.value}${idx}`}>{e.name}</MenuItem>);
    });
    return  options;
  };
  useEffect(()=>{
    if (props.noneStoresUndefined && (props.defaultValue === undefined || props.defaultValue === null)) {
      setCurrentSelected('');
    } else {
      setCurrentSelected(props.defaultValue);
    }
  }, [props.viewMode, props.noneStoresUndefined, props.defaultValue]);


  const verifyValueAndOptions = ()=> {
    if(props.defaultValue) {
      const allowedValues = props.options.map((opt)=> opt.value);
      if(!allowedValues.includes(props.defaultValue)) {
        return {name: props.defaultValue, value: props.defaultValue};

      }
    }
    return undefined;
  };

  const renderOptions = getOptionsToRender();

  useEffect(()=> {
    if (effectiveAllowNone && (props.defaultValue === undefined || props.defaultValue === null || props.defaultValue === '')) {
      setCurrentSelected('');
      return;
    }
    if (!props.defaultValue) return;
    if (!currentSelected) {
      setCurrentSelected(props.defaultValue);
    }
  }, [props.defaultValue, effectiveAllowNone]);

  const handleSelect = (e, fieldOnChange)=> {
    const next = e.target.value;
    setCurrentSelected(next);
    if (typeof fieldOnChange === 'function') {
      fieldOnChange(next);
    }
  };

  useEffect(()=> {
    const currValue = formMethods.getValues(props.field_name);
    const valueToSet = props.noneStoresUndefined && (currentSelected === '' || currentSelected === undefined)
      ? undefined
      : fieldFormatter.getValueForDB(props.field.api_name, currentSelected);
    //skip reset value to optimize performance of useFieldsArray subforms with multiple selects with default values
    //don't skip update if value expected to be transformed to boolean (Yes/No)
    if(!effectiveAllowNone && (!currentSelected ||  (currValue === currentSelected && typeof valueToSet !== 'boolean'))) return;
    formMethods.setValue(props.field_name, valueToSet, {
      shouldDirty: true, shouldTouch: true, shouldValidate: true
    });
  }, [currentSelected]);

  const handleMouseDown = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleClick = (event)=> {
    event.preventDefault();
    event.stopPropagation();
    setCurrentSelected('');
    props.setValueToForm(
      props.field_name,
      props.noneStoresUndefined ? undefined : '',
      {shouldDirty: true, shouldValidate: true}
    );

  };

  const endAdornment = () => {
    if(!props.canClean) return null;
    if (currentSelected && currentSelected.length !== 0) {
      return (
        <InputAdornment position="end">
          <IconButton
            onClick={handleClick}
            onMouseDown={handleMouseDown}
            color="secondary"
          >
            <ClearIcon />
          </IconButton>
        </InputAdornment>
      );
    }

    return '';
  };

  return (
    <>
      <Controller
        name={props.field_name}
        control={props.control}
        defaultValue={props.defaultValue}
        rules={props.field?.required ? {required: true} : undefined}
        render={({field}) => (
          <Select
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left'
              },
              // getContentAnchorEl: null,
            }}

            input={
              <CustomInput id={`${props.field_name}-input`}
                name={props.field_name.replace(/\./, '')}
                fullWidth={props.fullWidth}
                endAdornment={endAdornment()}
                error={props.hasError}
              />}
            name={field.name}
            onBlur={field.onBlur}
            inputRef={field.ref}
            ref={ref}
            onChange={(e) => { handleSelect(e, field.onChange); }}
            disabled={!props.isEditable || props.viewMode}
            displayEmpty={showEmptyDisplay}
            renderValue={
              showEmptyDisplay
                ? (selected) => {
                  if (selected === '' || selected == null) {
                    return '';
                  }
                  const match = props.options?.find((o) => o.value === selected);
                  return match?.name ?? selected;
                }
                : undefined
            }
            value={currentSelected ?? ''}
            error={props.isError}>
            {renderOptions}

          </Select>
        )}/>
    </>
  );
});
CustomSelect.displayName = 'CustomSelect';
CustomSelect.propTypes = {
  ...formBlockTypes,
  ...{
    field_name: PropTypes.string.isRequired,
    allowNone: PropTypes.bool,
    noneStoresUndefined: PropTypes.bool,
  }
};
CustomSelect.defaultProps = {
  ...formBlockDefaultProps,
  ...{
    allowNone: false,
    noneStoresUndefined: false,
  }
};
export default CustomSelect;
